import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "UX Strategy That Drives Business Results",
  seoDescription: "User experience strategy grounded in research and designed for conversion. We create experiences that users love and that drive business outcomes.",
  canonicalUrl: "https://xperfomax.com/ux-strategy",
  category: "services",
  categoryLabel: "Services / UX Strategy",
  headline: "UX Strategy That Drives Business Results",
  subheadline: "Great UX isn't about following trends. It's about understanding users and removing friction. We design experiences that convert.",
  ctaText: "Request a UX audit",
  problemTitle: "Why UX Projects Fail",
  problems: [
    "Design decisions are built on assumptions, not research.",
    "UX improvements are disconnected from business metrics.",
    "Siloed design ignores technical and resource constraints.",
    "One-time projects lack ongoing optimization and validation.",
  ],
  systemTitle: "Our UX Approach",
  systemDescription: "Research-led, business-aligned, and continuously validated.",
  services: [
    { name: "User Research", icon: "Users", components: ["User interviews & surveys", "Behavioral analytics review", "Persona development"], outcome: "Deep understanding of user needs, not assumptions." },
    { name: "UX Audits", icon: "Search", components: ["Heuristic evaluation", "Accessibility assessment", "Competitive analysis"], outcome: "Clear prioritized roadmap for UX improvements." },
    { name: "Information Architecture", icon: "Layers", components: ["Content hierarchy design", "Navigation structure", "User flow mapping"], outcome: "Intuitive structures that users navigate effortlessly." },
    { name: "Interaction Design", icon: "MousePointerClick", components: ["Microinteraction design", "Animation & feedback patterns", "Form & input optimization"], outcome: "Delightful interactions that guide user behavior." },
    { name: "Usability Testing", icon: "FlaskConical", components: ["Task-based testing", "Think-aloud protocols", "A/B & multivariate testing"], outcome: "Validated designs backed by real user behavior." },
    { name: "UX Documentation", icon: "FileText", components: ["Design system guidelines", "Component specifications", "Pattern libraries"], outcome: "Scalable documentation for consistent experiences." },
  ],
  processTitle: "From research to results.",
  processSteps: [
    { number: "01", title: "Discovery & Research", description: "Understand your users, business goals, and current experience gaps through research and analysis." },
    { number: "02", title: "Strategy Definition", description: "Define UX principles, success metrics, and a prioritized roadmap for improvements." },
    { number: "03", title: "Design & Prototyping", description: "Create wireframes, prototypes, and interaction designs based on strategic direction." },
    { number: "04", title: "Testing & Validation", description: "Validate designs through usability testing and iterate based on user feedback." },
    { number: "05", title: "Implementation Support", description: "Support development with specifications, QA, and design system documentation." },
  ],
  technologyCategories: [
    { title: "Research", icon: "Compass", items: ["UserTesting", "Maze", "Lookback", "Optimal Workshop"] },
    { title: "Design", icon: "PenTool", items: ["Figma", "Sketch", "Adobe XD", "Framer"] },
    { title: "Prototyping", icon: "TestTube", items: ["Figma Prototypes", "Principle", "ProtoPie"] },
    { title: "Analytics", icon: "LineChart", items: ["Hotjar", "FullStory", "Mixpanel", "Amplitude"] },
  ],
  techTitle: "UX Research & Design Tools",
  techDescription: "Industry-leading tools for research, design, and validation.",
  qualification: {
    forYou: [
      "You're experiencing friction in your user journey",
      "You want decisions based on research, not opinions",
      "You're ready to invest in understanding your users",
      "You have development resources to implement recommendations",
    ],
    notForYou: [
      "You just need a quick visual refresh",
      "You're not willing to talk to actual users",
      "You expect UX strategy without implementation follow-through",
      "You want to skip research and go straight to design",
    ],
  },
  faqs: [
    { question: "What's the difference between UX and UI design?", answer: "UX (User Experience) focuses on how a product works and feels. UI (User Interface) focuses on how it looks. We handle both, but UX strategy comes first to ensure the foundation is solid." },
    { question: "How long does a UX strategy project take?", answer: "A comprehensive UX strategy typically takes 4-8 weeks depending on scope. Quick audits can be completed in 2-3 weeks, while full redesign strategies may take 8-12 weeks." },
    { question: "Do you need access to our users for research?", answer: "Ideally yes, but we can also work with existing data, analytics, and recruit participants independently. Direct user access yields the best insights." },
    { question: "How do you measure UX success?", answer: "We define success metrics upfront: task completion rates, time-on-task, error rates, satisfaction scores, and ultimately conversion and retention improvements." },
    { question: "Do you handle implementation?", answer: "We provide detailed specifications and support your development team. We can also connect you with our development partners for full implementation." },
  ],
  finalCtaTitle: "Build experiences users love.",
  finalCtaDescription: "Good UX is good business. Let's design experiences that drive growth.",
  finalCtaButtonText: "Request a UX audit",
};

const UXStrategy = () => {
  const { content } = usePageContent("ux-strategy", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default UXStrategy;
